import { Menu, X, Camera } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { logout } from "../../store/slices/authSlice";
import { Logo } from "./Logo";

export default function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);

  const handleLogout = () => {
    dispatch(logout());
  };

  const openCameraModal = () => {
    window.dispatchEvent(new CustomEvent("open-camera-permission-modal"));
  };

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-purple-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center justify-between gap-3">
          <Logo size="md" className="shrink-0" />
          
          <div className="hidden md:flex items-center gap-2.5 md:gap-3.5 lg:gap-6 xl:gap-8 text-xs lg:text-sm font-medium">
            <a href="#features" className="text-gray-600 hover:text-purple-600 transition-colors whitespace-nowrap">
              Features
            </a>
            <Link to="/how-it-works" className="text-gray-600 hover:text-purple-600 transition-colors whitespace-nowrap">
              How it Works
            </Link>
            {user?.role !== "listener" && (
              <a href="#pricing" className="text-gray-600 hover:text-purple-600 transition-colors whitespace-nowrap">
                Pricing
              </a>
            )}
            {user ? (
              <>
                <Link 
                  to={user.role === 'admin' ? "/admin/dashboard" : user.role === 'listener' ? "/listener/dashboard" : "/dashboard"} 
                  className="text-gray-600 hover:text-purple-600 transition-colors font-medium whitespace-nowrap"
                >
                  {user.role === 'admin' ? "Admin Panel" : user.role === 'listener' ? "Listener Panel" : "Dashboard"}
                </Link>
                <button
                  onClick={openCameraModal}
                  className="flex items-center gap-1 px-2.5 py-1.5 lg:px-3 lg:gap-1.5 text-xs text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-full font-semibold border border-purple-200 transition-all whitespace-nowrap"
                  title="Camera Access & Permissions"
                >
                  <Camera className="w-3.5 h-3.5 text-purple-600 shrink-0" />
                  <span>Camera Access</span>
                </button>
                <button 
                  onClick={handleLogout}
                  className="px-3 py-1.5 lg:px-5 lg:py-2.5 text-xs lg:text-sm text-purple-600 hover:bg-purple-50 rounded-full transition-colors font-medium border border-purple-100 whitespace-nowrap"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="px-3.5 py-1.5 lg:px-5 lg:py-2.5 text-xs lg:text-sm text-purple-600 hover:bg-purple-50 rounded-full transition-colors whitespace-nowrap">
                  Sign In
                </Link>
                <Link to="/register" className="px-3.5 py-1.5 lg:px-5 lg:py-2.5 text-xs lg:text-sm bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full hover:shadow-lg transition-all whitespace-nowrap">
                  Get Started
                </Link>
              </>
            )}
          </div>

          <button 
            className="md:hidden p-2 text-gray-600 hover:text-purple-600 transition-colors"
            onClick={toggleMenu}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden mt-4 py-4 border-t border-purple-50 border-gray-100 animate-in fade-in slide-in-from-top-4 duration-200">
            <div className="flex flex-col gap-4">
              <a href="#features" className="text-lg font-medium text-gray-600 hover:text-purple-600 px-2" onClick={toggleMenu}>
                Features
              </a>
              <Link to="/how-it-works" className="text-lg font-medium text-gray-600 hover:text-purple-600 px-2" onClick={toggleMenu}>
                How it Works
              </Link>
              {user?.role !== "listener" && (
                <a href="#pricing" className="text-lg font-medium text-gray-600 hover:text-purple-600 px-2" onClick={toggleMenu}>
                  Pricing
                </a>
              )}
              {user ? (
                <>
                  <Link 
                    to={user.role === 'admin' ? "/admin/dashboard" : user.role === 'listener' ? "/listener/dashboard" : "/dashboard"} 
                    className="text-lg font-medium text-gray-600 hover:text-purple-600 px-2" 
                    onClick={toggleMenu}
                  >
                    {user.role === 'admin' ? "Admin Panel" : user.role === 'listener' ? "Listener Panel" : "Dashboard"}
                  </Link>
                  <button
                    onClick={() => { openCameraModal(); toggleMenu(); }}
                    className="w-fit flex items-center gap-2 px-5 py-2 text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-full font-semibold border border-purple-200 transition-all text-sm"
                  >
                    <Camera className="w-4 h-4 text-purple-600" />
                    <span>Camera & Permissions</span>
                  </button>
                  <button 
                    onClick={() => { handleLogout(); toggleMenu(); }}
                    className="w-fit px-6 py-2 text-purple-600 border border-purple-200 rounded-full font-medium"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <div className="flex flex-col gap-3 pt-2">
                  <Link to="/login" className="w-full px-6 py-2.5 text-center text-purple-600 border border-purple-100 rounded-full font-medium" onClick={toggleMenu}>
                    Sign In
                  </Link>
                  <Link to="/register" className="w-full px-6 py-2.5 text-center bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full font-medium" onClick={toggleMenu}>
                    Get Started
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
